import { useEffect, useState, useRef, useCallback } from "react";
import { useTranslation } from "../hooks/useTranslation";
import { useParams, useNavigate } from "react-router-dom";
import {
    FiArrowLeft,
    FiSearch,
    FiMoreHorizontal,
    FiMessageSquare,
    FiThumbsUp,
    FiShare,
    FiEdit2,
    FiMail,
    FiBookmark,
    FiCopy,
    FiType
} from "react-icons/fi";
import toast from "react-hot-toast";
import { API_BASE } from "../constants/index";

const GET_ANNOUNCEMENT_LIST_API = `${API_BASE}/publicApi/getAnnList`;

export default function NewsDetails() {
    const { t, language } = useTranslation();
    const { ann_id } = useParams();
    const navigate = useNavigate();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const menuRef = useRef(null);
    const [announcementData, setAnnouncementData] = useState(null);

    // Close menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setIsMenuOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const getAnnouncement = useCallback(async () => {
        try {
            const res = await fetch(GET_ANNOUNCEMENT_LIST_API, {
                method: "POST",
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    id: parseInt(ann_id)
                })
            });

            const responseData = await res.json();

            if (!res.ok) {
                // Preserve error details in the error object
                const error = new Error(responseData.message || responseData.error || `HTTP error! status: ${res.status}`);
                error.errorCode = responseData.errorCode || res.status;
                error.error = responseData.error || responseData.message;
                throw error;
            }

            const announcement = responseData.data;

            let title = announcement.ann_title_en_US;
            let content = announcement.ann_content_en_US;
            let file = announcement.ann_file_en_US;

            switch (language) {
                case 'zh':
                    title = announcement.ann_title_zh_CN || title;
                    content = announcement.ann_content_zh_CN || content;
                    file = announcement.ann_file_zh_CN || file;
                    break;
                case 'zh-TW':
                    title = announcement.ann_title_zh_TW || title;
                    content = announcement.ann_content_zh_TW || content;
                    file = announcement.ann_file_zh_TW || file;
                    break;
                case 'ko':
                    title = announcement.ann_title_ko_KR || title;
                    content = announcement.ann_content_ko_KR || content;
                    file = announcement.ann_file_ko_KR || file;
                    break;
                case 'ja':
                    title = announcement.ann_title_ja_JP || title;
                    content = announcement.ann_content_ja_JP || content;
                    file = announcement.ann_file_ja_JP || file;
                    break;
                case 'vi':
                    title = announcement.ann_title_vi_VN || title;
                    content = announcement.ann_content_vi_VN || content;
                    file = announcement.ann_file_vi_VN || file;
                    break;
                case 'id':
                    title = announcement.ann_title_id_ID || title;
                    content = announcement.ann_content_id_ID || content;
                    file = announcement.ann_file_id_ID || file;
                    break;
                case 'ta':
                    title = announcement.ann_title_ta_IN || title;
                    content = announcement.ann_content_ta_IN || content;
                    file = announcement.ann_file_ta_IN || file;
                    break;
                case 'hi':
                    title = announcement.ann_title_hi_IN || title;
                    content = announcement.ann_content_hi_IN || content;
                    file = announcement.ann_file_hi_IN || file;
                    break;
                default:
                    break;
            }

            setAnnouncementData({
                ann_title: title,
                ann_content: content,
                ann_file: file,
                ann_cdate: announcement.ann_cdate,
                ann_pdate: announcement.ann_pdate,
            });

        } catch (error) {
            console.error("Error getting announcement data:", error);
            toast.error(error);
        }
    }, [language]);

    useEffect(() => {
        getAnnouncement();
    }, [getAnnouncement]);


    return (
        <div className="flex flex-col pb-20">
            {/* Header */}
            <div className="flex justify-between items-center mb-8 text-white relative">
                <button onClick={() => navigate(-1)} className="hover:text-gray-300 transition-colors">
                    <FiArrowLeft size={24} />
                </button>
                <div className="flex gap-5">
                    {/* <button className="hover:text-gray-300 transition-colors">
                        <FiSearch size={24} />
                    </button> */}
                    <div className="relative" ref={menuRef}>
                        {/* <button
                            className="hover:text-gray-300 transition-colors flex items-center justify-center"
                            onClick={() => setIsMenuOpen(!isMenuOpen)}
                        >
                            <FiMoreHorizontal size={24} />
                        </button> */}

                        {/* Dropdown Menu */}
                        {isMenuOpen && (
                            <div className="absolute right-0 top-full mt-4 w-48 bg-[#0a0512] border border-[#231735] rounded-xl shadow-xl overflow-hidden z-50">
                                <ul className="text-[15px] text-gray-200">
                                    <li className="flex items-center gap-3 px-5 py-3.5 hover:bg-[#1a1226] cursor-pointer transition-colors border-b border-[#1a1226]">
                                        <FiMail className="text-gray-400" size={18} />
                                        <span>Notification</span>
                                    </li>
                                    <li className="flex items-center gap-3 px-5 py-3.5 hover:bg-[#1a1226] cursor-pointer transition-colors border-b border-[#1a1226]">
                                        <FiShare className="text-gray-400" size={18} />
                                        <span>Share</span>
                                    </li>
                                    <li className="flex items-center gap-3 px-5 py-3.5 hover:bg-[#1a1226] cursor-pointer transition-colors border-b border-[#1a1226]">
                                        <FiBookmark className="text-gray-400" size={18} />
                                        <span>Save</span>
                                    </li>
                                    <li className="flex items-center gap-3 px-5 py-3.5 hover:bg-[#1a1226] cursor-pointer transition-colors border-b border-[#1a1226]">
                                        <FiCopy className="text-gray-400" size={18} />
                                        <span>Copy Link</span>
                                    </li>
                                    <li className="flex items-center gap-3 px-5 py-3.5 hover:bg-[#1a1226] cursor-pointer transition-colors">
                                        <FiType className="text-gray-400" size={18} />
                                        <span>Font Size</span>
                                    </li>
                                </ul>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="flex-1">
                {/* Title */}
                <h1 className="text-[22px] md:text-2xl font-semibold text-white mb-4 leading-snug">
                    {announcementData?.ann_title}
                </h1>

                {/* Source and Date */}
                <div className="flex items-center text-sm mb-8">
                    <span className="text-gray-500">{new Date(announcementData?.ann_pdate).toLocaleDateString()}</span>
                </div>

                {/* Optional Image */}
                {announcementData?.ann_file && (
                    <div className="mb-6 flex justify-center">
                        <img
                            src={announcementData.ann_file}
                            alt={announcementData.ann_title || "News Image"}
                            className="rounded-2xl max-w-full h-auto object-contain"
                        />
                    </div>
                )}

                {/* Content */}
                <div className="text-gray-300 text-[15px] leading-relaxed space-y-4">
                    <div
                        className="text-gray-300 text-sm leading-relaxed"
                        dangerouslySetInnerHTML={{ __html: announcementData?.ann_content }}
                    />
                </div>
                {/*} */}
            </div>

            {/* Bottom Bar */}
            <div className="fixed bottom-0 left-0 right-0 p-4 bg-[#08030d]/95 backdrop-blur-md border-t border-[#231735] flex items-center justify-between z-50">
                <div className="max-w-4xl mx-auto flex items-center justify-between w-full">
                    <div className="flex items-center bg-[#150e1f] rounded-xl px-4 py-2.5 flex-1 mr-6">
                        <FiEdit2 className="text-gray-400 mr-3" size={18} />
                        <input
                            type="text"
                            placeholder="Write comment"
                            className="bg-transparent text-sm text-white focus:outline-none w-full placeholder-gray-500"
                        />
                    </div>
                    <div className="flex items-center gap-6 text-gray-300">
                        <button className="hover:text-white transition-colors">
                            <FiMessageSquare size={22} />
                        </button>
                        <button className="hover:text-white transition-colors">
                            <FiThumbsUp size={22} />
                        </button>
                        <button className="hover:text-white transition-colors">
                            <FiShare size={22} />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}